/**
 * CR-1154: Hyperlink on a Purchase Order number ("Sales Order" in supplier mode).
 *
 * CR-7278 (Aaron, 5 Oct 2026): on a row that is one PO line, pass `poLineId` and
 * the PO number opens that line's detail (the same target as its
 * {@link PoLineLink}). On a row that is a whole PO, or one with no line id, it
 * opens the PO header modal.
 *
 * Opens in a new tab, consistent with PoLineLink / GrnLink. Degrades to plain
 * text when there is no PO number.
 *
 * @param poNumber - The PO number. Empty/null renders children as plain text.
 * @param poLineId - The row's PO line, when the row is one line; opens its detail.
 * @param children - The label to render (defaults to the PO number).
 * @param className - Optional override for the link styling.
 * @returns A router link to the PO header, or the bare children.
 *
 * @example
 * <PoHeaderLink poNumber={grn.po_number} />
 */
import { Link } from 'react-router-dom';
import { ReactNode } from 'react';
import { poLineHref } from './PoLineLink';

interface PoHeaderLinkProps {
  poNumber?: string | null;
  /** CR-7278: the row's PO line, when the row is one line. */
  poLineId?: number | string | null;
  children?: ReactNode;
  className?: string;
}

/**
 * Deep-link for the PO header modal on the PO Headers tab (parsed in
 * Purchasing.tsx). CR-7278: the target of a PO number on a row that is a whole
 * PO, not one line.
 *
 * @param poNumber - The PO number.
 * @returns The PO Headers URL that opens the PO's header modal.
 */
export function poHeaderHref(poNumber: string): string {
  return `/purchasing?tab=po_headers&po=${encodeURIComponent(poNumber)}`;
}

/**
 * CR-7278: where a PO-number click goes — the line's detail when the row is one
 * PO line, otherwise the PO's header modal (Aaron, 5 Oct 2026).
 *
 * @param poNumber - The PO number.
 * @param poLineId - The row's PO line, if the row is one line.
 * @returns The line-detail URL, or the PO header modal URL.
 */
export function poNumberHref(poNumber: string, poLineId?: unknown): string {
  if (typeof poLineId === 'number' || (typeof poLineId === 'string' && poLineId !== '')) {
    return poLineHref(poLineId);
  }
  return poHeaderHref(poNumber);
}

export default function PoHeaderLink({
  poNumber,
  poLineId,
  children,
  className = 'text-primary hover:underline',
}: PoHeaderLinkProps) {
  const label = children ?? poNumber ?? '-';
  if (!poNumber) {
    return <>{label}</>;
  }
  return (
    <Link to={poNumberHref(poNumber, poLineId)} target="_blank" rel="noopener noreferrer" className={className}>
      {label}
    </Link>
  );
}
